import { createRouter, createRoute, createRootRoute, redirect, Outlet } from '@tanstack/react-router';
import { useAuthStore } from './store/authStore';
import { LoginPage } from './pages/Login';
import { RegisterPage } from './pages/Register';
import { ForgotPasswordPage } from './pages/ForgotPassword';
import { UsersPage } from './pages/Users';
import { LogsPage } from './pages/Logs';
import { SettingsPage } from './pages/Settings';
import { BillingPage } from './pages/Billing';
import { ProjectsPage } from './pages/Projects';
import { SsoCallbackPage } from './pages/SsoCallback';
import { TransmissionControlPage } from './pages/TransmissionControl';
import { RetiredRoutePage } from './pages/RetiredRoute';
import { DeviceAuthorizationPage } from './pages/DeviceAuthorization';
import { SecurityPage } from './pages/Security';

// Root route
const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// Auth check helper
function currentReturnTo(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function requireAuth() {
  const { isAuthenticated } = useAuthStore.getState();
  if (!isAuthenticated) {
    throw redirect({ to: '/login', search: { return_to: currentReturnTo() } });
  }
}

function requireGuest() {
  const { isAuthenticated } = useAuthStore.getState();
  if (isAuthenticated) {
    throw redirect({ to: '/transmission-control' });
  }
}

// Public routes
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: () => {
    requireGuest();
  },
  validateSearch: (search: Record<string, unknown>): { return_to?: string; sso_error?: string } => ({
    return_to: typeof search.return_to === 'string' ? search.return_to : undefined,
    sso_error: typeof search.sso_error === 'string' ? search.sso_error : undefined,
  }),
  component: LoginPage,
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  beforeLoad: () => {
    requireGuest();
  },
  component: RegisterPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  beforeLoad: () => {
    requireGuest();
  },
  component: ForgotPasswordPage,
});

const ssoCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: SsoCallbackPage,
});

// Protected routes
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    requireAuth();
    throw redirect({ to: '/transmission-control' });
  },
});

const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users',
  beforeLoad: () => {
    requireAuth();
  },
  component: UsersPage,
});

const projectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects',
  beforeLoad: () => {
    requireAuth();
  },
  component: ProjectsPage,
});

const transmissionControlRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/transmission-control',
  beforeLoad: () => {
    requireAuth();
  },
  component: TransmissionControlPage,
});

// Logs route with search params
const logsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/logs',
  beforeLoad: () => {
    requireAuth();
  },
  validateSearch: (search: Record<string, unknown>): { search?: string } => {
    return {
      search: typeof search.search === 'string' ? search.search : undefined,
    };
  },
  component: LogsPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  beforeLoad: () => {
    requireAuth();
  },
  component: SettingsPage,
});

const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing',
  beforeLoad: () => {
    requireAuth();
  },
  component: BillingPage,
});

const deviceAuthorizationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/device',
  beforeLoad: () => {
    requireAuth();
  },
  validateSearch: (search: Record<string, unknown>): { user_code?: string } => ({
    user_code: typeof search.user_code === 'string' ? search.user_code : undefined,
  }),
  component: DeviceAuthorizationPage,
});

const securityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/security',
  beforeLoad: () => {
    requireAuth();
  },
  component: SecurityPage,
});

const securityIncidentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/security/incidents/$incidentId',
  beforeLoad: () => {
    requireAuth();
  },
  component: SecurityPage,
});

const retiredRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '$',
  beforeLoad: () => {
    requireAuth();
  },
  component: RetiredRoutePage,
});

// Create the route tree
const routeTree = rootRoute.addChildren([
  loginRoute,
  registerRoute,
  forgotPasswordRoute,
  ssoCallbackRoute,
  indexRoute,
  usersRoute,
  projectsRoute,
  transmissionControlRoute,
  logsRoute,
  settingsRoute,
  billingRoute,
  deviceAuthorizationRoute,
  securityRoute,
  securityIncidentRoute,
  retiredRoute,
]);

// Create the router instance
export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
